import React, { useState } from 'react';
import { formatDate } from '../../../utils/utils';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'

import { ArrowTopRightOnSquareIcon, CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'

const apiUrl = process.env.REACT_APP_API_URL;

function SoftwarePortfolioItem({portfolio}) {
    const [selectedImage, setSelectedImage] = useState(0);
    const [openDetails, setOpenDetails] = useState(false);
    const images = portfolio.images || [];
    const hasImages = images.length > 0;

    const onNext = (event) => {
        event?.stopPropagation();

        if(selectedImage < images.length - 1){
            setSelectedImage(selectedImage + 1)
        } else {
            setSelectedImage(0);
        }
    }

    const onBack = (event) => {
        event?.stopPropagation();

        if(selectedImage > 0){
            setSelectedImage(selectedImage - 1)
        } else {
            setSelectedImage(images.length - 1);
        }
    }

    return (
        <>
        <article key={portfolio.id} className='group col-span-1 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-primary transition duration-300 hover:border-secondary/70 dark:border-white/10 dark:bg-white/10'>
            <div className='relative h-80 w-full overflow-hidden bg-primary/5 dark:bg-white/5'>
                {hasImages ? (
                    <img
                        src={`${apiUrl}${images[selectedImage].image_url}`}
                        alt={portfolio.title}
                        className='absolute h-full w-full object-cover transition duration-700 group-hover:scale-110'
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/20">
                        <p className="text-sm font-medium text-primary/70 dark:text-white/70">Project preview</p>
                    </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/15 to-transparent opacity-85" />

                <div className="absolute left-5 top-5 z-20 flex items-center gap-2">
                    <span className="rounded-lg border border-white/20 bg-white/15 px-3 py-1 text-xs font-medium text-white">
                        Featured project
                    </span>
                </div>

                {hasImages && images.length > 1 && (
                    <div className="absolute inset-x-0 top-1/2 z-30 flex -translate-y-1/2 flex-row justify-between px-4 opacity-0 transition duration-300 group-hover:opacity-100">
                        <button
                            type="button"
                            aria-label="Previous project image"
                            className='flex size-10 items-center justify-center rounded-lg border border-white/30 bg-white/90 text-primary transition duration-200 hover:bg-secondary-light'
                            onClick={onBack}
                        >
                            <ChevronLeftIcon className='size-5'/>
                        </button>
                        <button
                            type="button"
                            aria-label="Next project image"
                            className='flex size-10 items-center justify-center rounded-lg border border-white/30 bg-white/90 text-primary transition duration-200 hover:bg-secondary-light'
                            onClick={onNext}
                        >
                            <ChevronRightIcon className='size-5'/>
                        </button>
                    </div>
                )}

                {hasImages && images.length > 1 && (
                    <div className="absolute bottom-5 left-0 z-20 flex w-full flex-row justify-center gap-2">
                    {images.map((image, index) => {
                        return (
                            <div
                                key={image.id || image.image_url || index}
                                className={`h-1.5 rounded-full transition-all duration-300 ${index===selectedImage ? 'w-8 bg-secondary-light' : 'w-2 bg-white/60'}`}
                            >
                            </div>
                        );
                    })}
                    </div>
                )}
            </div>

            <div className="flex flex-grow flex-col gap-5 p-6">
                <div>
                    <p className='text-xs font-semibold uppercase tracking-[0.25em] text-secondary-dark dark:text-secondary'>Case study</p>
                    <h3 className='mt-2 text-2xl font-semibold tracking-tight text-gray-950 dark:text-white'>{portfolio.title}</h3>
                    <p className='mt-3 line-clamp-3 text-sm leading-6 text-gray-600 dark:text-gray-400'>{portfolio.description}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <p className='rounded-lg border border-primary/10 bg-primary/5 px-3 py-1 text-xs font-medium text-gray-600 dark:border-white/10 dark:bg-white/10 dark:text-gray-300'>Started <span className='text-primary dark:text-secondary'>{formatDate(portfolio.start_date)}</span></p>
                    <p className='rounded-lg border border-secondary/30 bg-secondary/10 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300'>Delivered <span className='text-primary dark:text-secondary'>{formatDate(portfolio.end_date)}</span></p>
                </div>

                <div className="flex flex-grow"></div>

                <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-5 dark:border-white/10">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Live preview available</p>
                    <div className="flex flex-wrap justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setOpenDetails(true)}
                            className='inline-flex w-fit flex-row items-center gap-2 rounded-lg border border-primary/15 bg-white px-4 py-2 text-sm font-medium text-primary transition duration-200 hover:border-secondary dark:border-white/10 dark:bg-white/10 dark:text-white'
                        >
                            Details
                        </button>
                        <a
                            href={portfolio.url}
                            target='_blank'
                            rel="noreferrer"
                            className='inline-flex w-fit flex-row items-center gap-2 rounded-lg border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition duration-200 hover:text-secondary'
                        >
                            <p>View project</p>
                            <ArrowTopRightOnSquareIcon className='size-4'/>
                        </a>
                    </div>
                </div>
            </div>
        </article>
        <ProjectDetailsModal
            open={openDetails}
            setOpen={setOpenDetails}
            portfolio={portfolio}
            images={images}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            onNext={onNext}
            onBack={onBack}
        />
        </>
    );
}

function ProjectDetailsModal({ open, setOpen, portfolio, images, selectedImage, setSelectedImage, onNext, onBack }) {
    const hasImages = images.length > 0;

    return (
        <Dialog open={open} onClose={setOpen} className="relative z-[60] text-primary">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-primary/60 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-3 text-center sm:items-center sm:p-6">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-6xl transform overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in dark:border-white/10 dark:bg-dark-card sm:my-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="relative grid max-h-[90vh] grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,1.35fr)_0.65fr]">
                            <div className="p-4 sm:p-6">
                                <div className="relative overflow-hidden rounded-[1.5rem] bg-primary/5 dark:bg-white/5">
                                    <div className="relative h-[24rem] sm:h-[34rem]">
                                        {hasImages ? (
                                            <img
                                                src={`${apiUrl}${images[selectedImage].image_url}`}
                                                alt={portfolio.title}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/20">
                                                <p className="text-sm font-medium text-primary/70 dark:text-white/70">Project preview</p>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent" />

                                        {hasImages && images.length > 1 && (
                                            <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-4">
                                                <button
                                                    type="button"
                                                    aria-label="Previous project image"
                                                    onClick={onBack}
                                                    className="flex size-11 items-center justify-center rounded-lg border border-gray-200 bg-white/90 text-primary transition duration-200 hover:bg-secondary-light"
                                                >
                                                    <ChevronLeftIcon className="size-5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-label="Next project image"
                                                    onClick={onNext}
                                                    className="flex size-11 items-center justify-center rounded-lg border border-gray-200 bg-white/90 text-primary transition duration-200 hover:bg-secondary-light"
                                                >
                                                    <ChevronRightIcon className="size-5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {hasImages && images.length > 1 && (
                                    <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-6">
                                        {images.map((image, index) => (
                                            <button
                                                key={image.id || image.image_url || index}
                                                type="button"
                                                onClick={() => setSelectedImage(index)}
                                                className={`relative h-20 overflow-hidden rounded-lg ring-2 transition duration-200 ${index === selectedImage ? 'ring-secondary' : 'ring-transparent hover:ring-primary/20'}`}
                                            >
                                                <img
                                                    src={`${apiUrl}${image.image_url}`}
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                                <span className={`absolute inset-0 ${index === selectedImage ? 'bg-primary/0' : 'bg-primary/25'}`} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="relative flex flex-col gap-6 border-t border-gray-100 bg-white/90 p-6 dark:border-white/10 dark:bg-dark-card/95 lg:border-l lg:border-t-0">
                                <button
                                    type="button"
                                    aria-label="Close project details"
                                    onClick={() => setOpen(false)}
                                    className="absolute right-5 top-5 flex size-10 items-center justify-center rounded-lg bg-slate-100 text-gray-700 transition duration-200 hover:bg-primary hover:text-white dark:bg-white/10 dark:text-white"
                                >
                                    <XMarkIcon className="size-5" />
                                </button>

                                <div className="pr-12">
                                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-secondary-dark dark:text-secondary">Project details</p>
                                    <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-950 dark:text-white">{portfolio.title}</h2>
                                    <p className="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-400">{portfolio.description}</p>
                                </div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
                                    <div className="rounded-lg border border-primary/10 bg-primary/5 p-4 dark:border-white/10 dark:bg-white/10">
                                        <div className="flex items-center gap-3">
                                            <CalendarDaysIcon className="size-5 text-primary dark:text-secondary" />
                                            <p className="text-sm font-semibold text-gray-950 dark:text-white">Started</p>
                                        </div>
                                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{formatDate(portfolio.start_date)}</p>
                                    </div>
                                    <div className="rounded-lg border border-secondary/30 bg-secondary/10 p-4">
                                        <div className="flex items-center gap-3">
                                            <CalendarDaysIcon className="size-5 text-primary dark:text-secondary" />
                                            <p className="text-sm font-semibold text-gray-950 dark:text-white">Delivered</p>
                                        </div>
                                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{formatDate(portfolio.end_date)}</p>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-gray-100 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/10">
                                    <h3 className="text-sm font-semibold text-gray-950 dark:text-white">What this project includes</h3>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {['UI/UX', 'Responsive build', 'Web platform', 'Production ready'].map((item) => (
                                            <span key={item} className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-100 dark:bg-white/10 dark:text-gray-300 dark:ring-white/10">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-auto flex flex-wrap gap-3 border-t border-gray-100 pt-5 dark:border-white/10">
                                    <a
                                        href={portfolio.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 rounded-lg border border-primary bg-primary px-5 py-3 text-sm font-medium text-white transition duration-200 hover:text-secondary"
                                    >
                                        Open live project
                                        <ArrowTopRightOnSquareIcon className="size-4" />
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        className="rounded-lg border border-gray-200 bg-white px-5 py-3 text-sm font-medium text-gray-700 transition duration-200 hover:border-secondary hover:text-primary dark:border-white/10 dark:bg-white/10 dark:text-white"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}

export default SoftwarePortfolioItem
